/**
 * @lume/shared — O VOCABULÁRIO DE ESCOPO como chave OPACA (Frente C1 — Motor Neutro).
 *
 * O eixo «escopo de papel» decide acesso por uma comparação de **igualdade de chave**
 * (`roleAllowsAction` → `grantedScopes.includes(requiredScope)`). Hoje as chaves são
 * vocabulário SETORIAL cravado em código, em **três** lugares que não se conhecem:
 *
 *   (a) o que a AÇÃO exige      — `INTENT_ACTION_SCOPE_DEFAULT` / `_BY_VERTICAL` (8 chaves)
 *   (b) o que o TITULAR concede — `BOND_AUTHORIZATION_SCOPES` (21 chaves; o DTO valida contra ele)
 *   (c) o que o PAPEL concede   — `Playbook.rolePermissions[].allowedScopes` (string livre)
 *
 * Este módulo **não** unifica os três (unificar LIBERA acesso hoje negado — §2.2 da spec, e é
 * decisão de PRODUTO do TL). Ele faz a coisa neutra: trata o escopo como **chave opaca**, deriva
 * mecanicamente o vocabulário EXIGIDO a partir da fonte, e produz o **relatório de coerência**
 * que torna a divergência visível — na superfície de AUTORIA e num guard que morde.
 *
 * PURO/TOTAL/sem I/O/sem lançar (molde A1/A2/B5/B6).
 *
 * ★ **NUNCA normaliza a chave.** O escopo é comparado por igualdade estrita; `trim`/lowercase no
 * caminho de decisão só pode AFROUXAR (lição B6 — o `sanitizeScopes` «defensivo» era o vetor).
 * Higienizar vocabulário é papel da superfície de AUTORIA (o DTO já tem a whitelist).
 *
 * ⚠️ Nada é computado em tempo de MODULE-LOAD sobre constantes importadas: o barrel raiz do
 * shared cria ciclos e `__exportStar` copiaria exports parciais (lição da fatia Simetria; a
 * `action-risk-config.ts` já paga esse preço com o `SEVERITY_VALUES`). Tudo resolve na CHAMADA.
 */
import { INTENT_ACTIONS, intentScope, scopeOverrideVerticals, } from './intent-actions';
import { IDENTITY_PROOFING_POLICIES } from './proofing-policy';
import { isConsequentialAction } from './risk-category';
/**
 * Os verticais sobre os quais o vocabulário exigido é derivado — **lidos da FONTE**, nunca de
 * uma lista manual. É o que impede o «guard que afirma cobertura AMPLA lendo escopo ESTREITO»
 * (lição B6 I3): se um vertical novo entrar em qualquer dos dois mapas, ele entra aqui sozinho.
 *
 * `null` = o default de plataforma. Vertical desconhecido resolve ao default (`intentScope` cai
 * em `INTENT_ACTION_SCOPE_DEFAULT`), logo não acrescenta chave nenhuma ao vocabulário.
 */
export function scopeVocabularyVerticals() {
    const seen = new Set();
    const out = [null];
    for (const v of [...Object.keys(IDENTITY_PROOFING_POLICIES), ...scopeOverrideVerticals()]) {
        if (seen.has(v))
            continue;
        seen.add(v);
        out.push(v);
    }
    return out;
}
/**
 * O vocabulário EXIGIDO: toda chave que alguma (ação, vertical) pede. Derivação **pura e
 * EXPORTADA** — a garantia não pode viver só no texto de um guard (lição B6 I4).
 *
 * Sem memo, de propósito: são `verticais × ações` = **119** lookups em `Record`, e a pureza vale
 * mais que o cache — quando o vocabulário exigido virar config POR TENANT, um cache global de
 * módulo estaria simplesmente errado (e o erro seria silencioso).
 */
export function collectRequiredScopes(opts) {
    const verticals = opts?.verticals ?? scopeVocabularyVerticals();
    const required = new Set();
    for (const vertical of verticals) {
        for (const action of INTENT_ACTIONS) {
            const scope = intentScope(action, vertical);
            if (scope !== null)
                required.add(scope);
        }
    }
    return required;
}
const sorted = (s) => [...new Set(s)].sort();
/**
 * Confronta os vocabulários. Não decide nada e não conserta nada — mede. As duas direções
 * importam: **órfão** (o gate pede o que ninguém pode conceder) e **morto** (concede-se o que
 * o gate nunca pede). As duas produzem a mesma experiência: a autorização não tem efeito.
 */
export function auditScopeVocabulary(params) {
    const { required, grantable } = params;
    const grantableSet = new Set(grantable);
    return {
        ungrantable: sorted([...required].filter((s) => !grantableSet.has(s))),
        deadGrantable: sorted(grantable.filter((s) => !required.has(s))),
        deadPolicy: sorted((params.policy ?? []).filter((s) => !required.has(s))),
    };
}
/**
 * A chave concedida tem algum efeito possível no gate? **Igualdade estrita** — sem `trim`, sem
 * caixa, sem alias (§ do cabeçalho). Usada SÓ pela superfície de AUTORIA; o caminho de DECISÃO
 * (`roleAllowsAction`) não a chama e permanece byte-idêntico.
 */
export function isScopeEffective(scope, required) {
    return (required ?? collectRequiredScopes()).has(scope);
}
/**
 * Os escopos CONCEDIDOS que nenhuma ação da plataforma jamais exige — a informação acionável
 * que a superfície de autoria hoje esconde (o operador concede e a tela confirma sucesso).
 *
 * Claim do tamanho EXATO da prova: «nenhuma ação, em nenhum vertical conhecido, exige esta
 * chave». NÃO afirma «este mandato é inútil neste vínculo» (isso dependeria do vertical em
 * jogo, e seria claim mais larga que a medição — lição B6 sobre monotonia).
 *
 * Preserva a ordem de entrada (a UI mostra na ordem em que o titular concedeu).
 */
export function ineffectiveScopes(granted, required) {
    const req = required ?? collectRequiredScopes();
    return granted.filter((s) => !req.has(s));
}
/**
 * PISO inafrouxável do eixo de escopo (Decisão 2 — «conteúdo vira config, **mas com piso**»).
 *
 * A chave é OPACA: não há ordem entre nomes, logo não existe «apertar» comparando chaves. O
 * único eixo ordenável é **ter ou não ter gate de papel** — e é exatamente aí que a config pode
 * afrouxar. Regra: a config **não pode REMOVER** (`null`) o gate de papel de uma ação
 * **CONSEQUENTE** que o seed de plataforma gateia. Trocar a chave por outra é permitido (troca
 * de vocabulário ≠ rebaixamento); exigir escopo onde o seed não exigia é permitido (só endurece).
 *
 * A peça que decide «consequente» é `isConsequentialAction` — **não** `isMutationToolKind`, que
 * é fail-OPEN no desconhecido e cego aos writes NATIVOS (a armadilha que a B5 pagou p/ descobrir).
 *
 * ⚠️ O piso vale para o OVERRIDE, com o SEED como baseline — molde exato do `classifyMappingRisk`
 * da A3, **inclusive na limitação declarada** (quando existir autoria VERTICAL em DB, ela terá de
 * entrar no baseline). O seed comercial rebaixa `service_action` a `null` deliberadamente
 * (PA2/Decisão 2 — autoria de PLATAFORMA, revisada em código, não config de tenant).
 */
export function applyScopeFloor(action, seedScope, proposedScope) {
    if (proposedScope !== null)
        return proposedScope;
    if (seedScope === null)
        return null;
    return isConsequentialAction(action) ? seedScope : null;
}
/**
 * Resolve o escopo exigido de forma config-aware: `override do tenant ▸ seed de plataforma`,
 * SEMPRE aplicando `applyScopeFloor` **quando há autoria**. Override ausente ⇒ devolve o seed
 * direto (o piso seria a identidade — o resultado é idêntico em 136/136, provado no differential;
 * o caminho é que é mais curto, e o docblock diz o que o código faz).
 *
 * ★ `undefined` EXPLÍCITO herda o seed, não remove o gate (Fable M2): `{cadastral_change: undefined}`
 * satisfaz `action in authored` e um `?? null` o transformaria em «sem gate de papel» — afrouxamento
 * nascido de ausência de valor. Ausência é HERANÇA; remover exige dizer `null`. Hoje o TS barra
 * (`exactOptionalPropertyTypes`), mas o writer futuro é JSON de config e não será barrado por tipo.
 */
export function resolveActionScope(action, vertical, opts) {
    const seed = intentScope(action, vertical);
    const authored = opts?.override?.byAction;
    if (authored === undefined)
        return seed;
    const proposed = authored[action];
    if (proposed === undefined)
        return seed;
    return applyScopeFloor(action, seed, proposed);
}
/**
 * Portão de aprovação de plataforma para a AUTORIA do escopo (mitigação do residual — Decisão 2).
 * Dispara quando a proposta REMOVE o gate de papel de uma ação consequente que o seed gateia — o
 * único rebaixamento representável num eixo de chaves opacas. Mecânico e neutro: sem lista
 * cravada de ações, sem juízo algum sobre o NOME da chave proposta.
 */
export function classifyScopeMappingRisk(action, vertical, proposedScope) {
    const seed = intentScope(action, vertical);
    const lowers = proposedScope === null && seed !== null && isConsequentialAction(action);
    return {
        requiresPlatformApproval: lowers,
        reason: lowers
            ? `remove o gate de papel de uma ação CONSEQUENTE (o seed exige "${seed}") — aprovação de plataforma exigida`
            : 'não remove o gate de papel de ação consequente',
    };
}
//# sourceMappingURL=scope-vocabulary.js.map